/**
 * Tipi TypeScript per Document Schemas
 * Corrispondono agli schemi Pydantic del backend
 */

/**
 * Attribute Data Type enum
 */
export enum AttributeDataType {
    STRING = "string",
    NUMBER = "number",
    DATE = "date",
    DATETIME = "datetime",
    BOOLEAN = "boolean",
    ARRAY = "array",
    OBJECT = "object",
    EMAIL = "email",
    URL = "url",
    PHONE = "phone",
}

/**
 * Document Schema Response
 */
export interface DocumentSchemaResponse {
    document_schema_id: string;
    document_type: string;
    document_type_id: string | null;
    schema_name: string;
    schema_code: string;
    version: string;
    description: string | null;
    use_cases: string | null;
    region: string | null;
    country_codes: string[] | null;
    carrier_codes: string[] | null;
    is_default: boolean;
    is_active: boolean;
    attribute_count: number;
    document_count: number;
    last_used_at: string | null;
    attribute_metadata: Record<string, unknown> | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

/**
 * Document Schema Create Request
 */
export interface DocumentSchemaCreate {
    document_type?: string;
    document_type_id?: string; // UUID as string
    schema_name: string;
    schema_code: string;
    version?: string;
    description?: string | null;
    use_cases?: string | null;
    region?: string | null;
    country_codes?: string[] | null;
    carrier_codes?: string[] | null;
    is_default?: boolean;
    is_active?: boolean;
    attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Document Schema Update Request
 */
export interface DocumentSchemaUpdate {
    schema_name?: string | null;
    description?: string | null;
    use_cases?: string | null;
    region?: string | null;
    country_codes?: string[] | null;
    carrier_codes?: string[] | null;
    is_default?: boolean | null;
    is_active?: boolean | null;
    version?: string | null;
    attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Document Schema List Filters
 */
export interface DocumentSchemaListFilters {
    skip?: number;
    limit?: number;
    document_type?: string;
    document_type_id?: string;
    region?: string;
    is_active?: boolean;
}

/**
 * Document Attribute Response
 */
export interface DocumentAttributeResponse {
    document_attribute_id: string;
    attribute_key: string;
    label: string;
    description: string | null;
    data_type: AttributeDataType;
    validation_rules: Record<string, unknown> | null;
    enum_values: unknown[] | null;
    input_type: string | null;
    placeholder: string | null;
    help_text: string | null;
    category: string | null;
    display_order: number;
    is_global: boolean;
    usage_count: number;
    attribute_metadata: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

/**
 * Document Attribute Create Request
 */
export interface DocumentAttributeCreate {
    attribute_key: string;
    label: string;
    description?: string | null;
    data_type: AttributeDataType;
    validation_rules?: Record<string, unknown> | null;
    enum_values?: unknown[] | null;
    input_type?: string | null;
    placeholder?: string | null;
    help_text?: string | null;
    category?: string | null;
    display_order?: number;
    is_global?: boolean;
    attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Document Attribute Update Request
 */
export interface DocumentAttributeUpdate {
    label?: string | null;
    description?: string | null;
    validation_rules?: Record<string, unknown> | null;
    enum_values?: unknown[] | null;
    input_type?: string | null;
    placeholder?: string | null;
    help_text?: string | null;
    category?: string | null;
    display_order?: number | null;
    is_global?: boolean | null;
    attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Document Attribute List Filters
 */
export interface DocumentAttributeListFilters {
    skip?: number;
    limit?: number;
    data_type?: AttributeDataType;
    category?: string;
    is_global?: boolean;
}

/**
 * Schema Attribute (Junction) Response
 */
export interface SchemaAttributeResponse {
    document_schema_attribute_id: string;
    document_schema_id: string;
    document_attribute_id: string;
    importance_level: number;
    is_required: boolean;
    display_order: number;
    custom_label: string | null;
    custom_help_text: string | null;
    custom_validation_rules: Record<string, unknown> | null;
    default_value: string | null;
    attribute_metadata: Record<string, unknown> | null;
    attribute_key: string | null;
    attribute_label: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Schema Attribute Create Request
 */
export interface SchemaAttributeCreate {
    document_attribute_id: string;
    importance_level?: number;
    is_required?: boolean;
    display_order?: number;
    custom_label?: string | null;
    custom_help_text?: string | null;
    custom_validation_rules?: Record<string, unknown> | null;
    default_value?: string | null;
    attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Schema Attribute Update Request
 */
export interface SchemaAttributeUpdate {
    importance_level?: number | null;
    is_required?: boolean | null;
    display_order?: number | null;
    custom_label?: string | null;
    custom_help_text?: string | null;
    custom_validation_rules?: Record<string, unknown> | null;
    default_value?: string | null;
    attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Schema Attribute List Filters
 */
export interface SchemaAttributeListFilters {
    skip?: number;
    limit?: number;
    importance_level?: number;
}

/**
 * Document Schema Rule Response
 */
export interface DocumentSchemaRuleResponse {
    document_schema_rule_id: string;
    document_schema_id: string;
    document_type: string;
    name: string;
    description: string | null;
    prompt: string;
    priority: number;
    is_active: boolean;
    target_attribute_ids: string[] | null;
    expected_output_format: Record<string, unknown> | null;
    test_cases: unknown[] | null;
    execution_count: number;
    success_count: number;
    last_executed_at: string | null;
    avg_confidence_score: number | null;
    rule_metadata: Record<string, unknown> | null;
    version: string;
    parent_rule_id: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

/**
 * Document Schema Rule Create Request
 */
export interface DocumentSchemaRuleCreate {
    name: string;
    document_type: string;
    prompt: string;
    description?: string | null;
    priority?: number;
    is_active?: boolean;
    target_attribute_ids?: string[] | null;
    expected_output_format?: Record<string, unknown> | null;
    test_cases?: unknown[] | null;
    rule_metadata?: Record<string, unknown> | null;
    version?: string;
}

/**
 * Document Schema Rule Update Request
 */
export interface DocumentSchemaRuleUpdate {
    name?: string | null;
    prompt?: string | null;
    description?: string | null;
    priority?: number | null;
    is_active?: boolean | null;
    target_attribute_ids?: string[] | null;
    expected_output_format?: Record<string, unknown> | null;
    test_cases?: unknown[] | null;
    rule_metadata?: Record<string, unknown> | null;
    version?: string | null;
}

/**
 * Document Schema Rule List Filters
 */
export interface DocumentSchemaRuleListFilters {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    document_type?: string;
}

/**
 * Attribute usage detail for a single schema-attribute junction
 */
export interface AttributeSchemaUsageDetail {
    document_schema_attribute_id: string;
    document_schema_id: string;
    schema_name: string;
    schema_code: string;
    document_type: string | null;
    document_type_id: string | null;
    document_type_name: string | null;
    importance_level: number;
    is_required: boolean;
    display_order: number;
    custom_label: string | null;
    custom_help_text: string | null;
}

/**
 * Full attribute usage detail response
 */
export interface AttributeUsageDetailResponse {
    document_attribute_id: string;
    attribute_key: string;
    label: string;
    usage_count: number;
    schemas: AttributeSchemaUsageDetail[];
}
